import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Plus, Edit, Trash, Check, Xmark, Lock } from 'iconoir-react';
import { toast } from 'sonner';

// Manage custom responsibility tags. Defaults are read-only — they appear
// muted with a lock icon and a small "Built-in" pill so users understand
// they can't edit/delete those (per product decision: keeps the dropdowns
// stable across weddings).
export function ResponsibilityTagsManager({
  open, onOpenChange, defaults, custom, onCreate, onUpdate, onDelete,
}) {
  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState('custom');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    const name = newName.trim();
    if (!name) {
      toast.error('Tag name is required');
      return;
    }
    setCreating(true);
    try {
      await onCreate({ name, kind: newKind });
      setNewName('');
      setNewKind('custom');
      toast.success(`Added "${name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add tag');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (tag) => {
    setEditingId(tag.id);
    setEditName(tag.name);
  };

  const saveEdit = async () => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    try {
      await onUpdate(editingId, { name: trimmed });
      toast.success('Tag renamed');
      setEditingId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not rename');
    }
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(`Delete "${tag.name}"? This will remove it from any timeline items that use it.`)) return;
    try {
      await onDelete(tag.id);
      toast.success('Tag deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete');
    }
  };

  const groupedDefaults = {
    role: defaults.filter((t) => t.kind === 'role'),
    vendor: defaults.filter((t) => t.kind === 'vendor'),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manage-tags-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Manage responsibility tags</DialogTitle>
          <DialogDescription className="text-muted">
            Built-in tags are always available. Add custom tags for anything specific to your wedding (e.g., "Mama Ji", "Aunty Ritu", "Live Painter").
          </DialogDescription>
        </DialogHeader>

        {/* Add new */}
        <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-2 pt-2" data-testid="add-tag-form">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New tag name (e.g., Phupho Ji)"
            className="input-field h-11 flex-1"
            data-testid="new-tag-name-input"
          />
          <select
            value={newKind}
            onChange={(e) => setNewKind(e.target.value)}
            className="h-11 px-3 rounded-sm border border-border bg-white text-sm text-charcoal"
            data-testid="new-tag-kind-select"
          >
            <option value="custom">Custom</option>
            <option value="role">Wedding role</option>
            <option value="vendor">Vendor team</option>
          </select>
          <Button
            type="submit"
            disabled={creating || !newName.trim()}
            className="btn-primary h-11 flex items-center gap-2"
            data-testid="add-tag-btn"
          >
            <Plus className="w-4 h-4" />
            {creating ? 'Adding...' : 'Add'}
          </Button>
        </form>

        {/* Custom tags */}
        <div className="pt-4">
          <h4 className="text-xs uppercase tracking-wider text-muted mb-2">
            Your custom tags ({custom.length})
          </h4>
          {custom.length === 0 ? (
            <p className="text-sm text-muted italic px-1">
              No custom tags yet — add any extra family members or specialty vendors above.
            </p>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-sm" data-testid="custom-tags-list">
              {custom.map((tag) => (
                <li key={tag.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`custom-tag-${tag.id}`}>
                  {editingId === tag.id ? (
                    <>
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="input-field h-9 flex-1"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit();
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        data-testid={`edit-tag-input-${tag.id}`}
                      />
                      <button onClick={saveEdit} className="p-2 text-moss hover:text-moss/70" data-testid={`save-tag-${tag.id}`}>
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={() => setEditingId(null)} className="p-2 text-muted hover:text-charcoal" data-testid={`cancel-edit-${tag.id}`}>
                        <Xmark className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-sm text-charcoal truncate">{tag.name}</span>
                        <Badge variant="outline" className="text-[10px] uppercase">
                          {tag.kind}
                        </Badge>
                      </div>
                      <button onClick={() => startEdit(tag)} className="p-2 text-muted hover:text-charcoal" data-testid={`edit-tag-${tag.id}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(tag)} className="p-2 text-muted hover:text-terracotta" data-testid={`delete-tag-${tag.id}`}>
                        <Trash className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Built-in (read-only) */}
        <div className="pt-4">
          <h4 className="text-xs uppercase tracking-wider text-muted mb-2 flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Built-in tags
          </h4>
          <div className="space-y-3">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted mb-1.5">Wedding roles</p>
              <div className="flex flex-wrap gap-1.5">
                {groupedDefaults.role.map((t) => (
                  <Badge key={t.id} variant="outline" className="text-xs font-normal text-charcoal/70">
                    {t.name}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted mb-1.5">Vendor teams</p>
              <div className="flex flex-wrap gap-1.5">
                {groupedDefaults.vendor.map((t) => (
                  <Badge key={t.id} variant="outline" className="text-xs font-normal text-charcoal/70">
                    {t.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button onClick={() => onOpenChange(false)} className="btn-secondary" data-testid="close-tags-manager-btn">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
